import React, { useState, useEffect, useRef } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  rectIntersection,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { twMerge } from 'tailwind-merge';
import { TbCalendar, TbEdit, TbTrash } from 'react-icons/tb';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Task, TaskStatus } from '../../../models/taskService.model';
import { taskService } from '../../../services/community/taskService';
import { formatDateAndTime } from '../../../utils/stringUtils';

const getStatusLabel = (status: TaskStatus, t: (key: string) => string): string => {
  if (status.systemKey === 'OPEN') return t('defaultOpenStatus');
  if (status.systemKey === 'CLOSED') return t('defaultClosedStatus');
  return status.name;
};

// local component used only inside taskboard
function StatusMenu({
  task,
  statuses,
  communityId,
  onChange,
}: {
  task: Task;
  statuses: TaskStatus[];
  communityId?: number;
  onChange: () => void;
}) {
  const { t } = useTranslation('', { keyPrefix: 'page.lists.task' });
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const selected = statuses.find((s) => s.statusId === task.status?.statusId);

  // Close when clicking outside the menu
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleSelect = async (status?: TaskStatus) => {
    if (!task.taskId || !communityId) return;
    if (status) {
      await taskService.moveToStatus(task, status, communityId);
    }
    setOpen(false);
    onChange();
  };

  return (
    <div ref={menuRef} className="relative">
      <button
        type="button"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="h-5 w-5 flex-shrink-0 rounded-full border border-secondary-200"
        style={{ backgroundColor: selected?.color || '#6B7280' }}
      />
      {open && (
        <div className="absolute z-50 mt-1 w-32 overflow-hidden rounded-lg border border-secondary-200 bg-secondary-50 shadow-lg">
          {statuses.map((s) => (
            <button
              key={s.statusId}
              type="button"
                onPointerDown={(e) => e.stopPropagation()}
              onClick={() => handleSelect(s)}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-primary-900 hover:bg-secondary-200">
              <span
                className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full"
                style={{ backgroundColor: s.color }}
              />
              {getStatusLabel(s, t)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

interface TaskBoardProps {
  tasks: Task[];
  statuses: TaskStatus[];
  communityId?: number;
  onMove: () => void;
  handleUpdate: (task: Task) => void;
  refreshTable: () => void;
}

export default function TaskBoard({ tasks, statuses, communityId, onMove, handleUpdate, refreshTable }: TaskBoardProps) {
  const [activeTask, setActiveTask] = useState<Task | null>(null);
  const [localStatusMap, setLocalStatusMap] = useState<Record<number, number>>({});
  const { t } = useTranslation('', { keyPrefix: 'page.lists.task' });

  // Remove stale optimistic overrides once the parent's tasks prop catches up with the server
  useEffect(() => {
    setLocalStatusMap((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      const cleaned = { ...prev };
      let changed = false;
      Object.keys(cleaned).forEach((taskIdStr) => {
        const taskId = Number(taskIdStr);
        const match = tasks.find((tsk) => tsk.taskId === taskId);
        if (!match || match.status?.statusId === cleaned[taskId]) {
          delete cleaned[taskId];
          changed = true;
        }
      });
      return changed ? cleaned : prev;
    });
  }, [tasks]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const fallbackStatuses: TaskStatus[] =
    statuses.length > 0
      ? statuses
      : [
          { statusId: -1, name: 'Open', color: '#3B82F6', order: 0, systemKey: 'OPEN' },
          { statusId: -2, name: 'Closed', color: '#6B7280', order: 999, systemKey: 'CLOSED' },
        ];

  const getEffectiveStatusId = (task: Task): number | undefined => {
    if (task.taskId !== undefined && localStatusMap[task.taskId] !== undefined) {
      return localStatusMap[task.taskId];
    }
    if (task.status?.statusId !== undefined) {
      return task.status.statusId;
    }
    // if the community has custom statuses but this task was never assigned,
    // fall back to the first one so it doesn't vanish from the board.
    if (statuses.length > 0) {
      return statuses[0].statusId;
    }
    return undefined;
  };

  const onDragStart = (event: DragStartEvent) => {
    const found = tasks.find((task) => String(task.taskId) === String(event.active.id));
    if (found) setActiveTask(found);
  };

  const onDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveTask(null);
    if (!over || !communityId) return;

    const task = tasks.find((tsk) => String(tsk.taskId) === String(active.id));
    if (!task || task.taskId === undefined) return;
    const { taskId } = task;

    let targetStatusId = over.data.current?.statusId as number | undefined;
    if (targetStatusId === undefined) {
      targetStatusId = fallbackStatuses.find((s) => String(s.statusId) === String(over.id))?.statusId;
    }

    if (targetStatusId === undefined) return;
    const nextStatusId: number = targetStatusId;
    if (getEffectiveStatusId(task) === nextStatusId) return;

    const targetStatus = fallbackStatuses.find((s) => s.statusId === nextStatusId);
    if (!targetStatus) return;

    // Optimistic update: show the card in the target column immediately
    setLocalStatusMap((prev) => ({ ...prev, [taskId]: nextStatusId }));

    try {
      await taskService.moveToStatus(task, targetStatus, communityId);
      onMove(); // triggers parent re-fetch; useEffect above cleans up localStatusMap once tasks prop updates
    } catch {
      // Revert optimistic update
      setLocalStatusMap((prev) => { const n = { ...prev }; delete n[taskId]; return n; });
      toast.error(t('moveError'));
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={rectIntersection}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}>
      <div className="flex gap-4 overflow-x-auto pb-4" style={{ minHeight: '400px' }}>
        {fallbackStatuses.map((status) => {
          const columnTasks =
            status.statusId < 0
              ? tasks.filter((tsk) => !tsk.status?.statusId)
              : tasks.filter((tsk) => getEffectiveStatusId(tsk) === status.statusId);

          return (
            <BoardColumn
              key={status.statusId}
              status={status}
              tasks={columnTasks}
              allStatuses={fallbackStatuses}
              handleUpdate={handleUpdate}
              refreshTable={refreshTable}
              communityId={communityId}
            />
          );
        })}
      </div>
      <DragOverlay>
        {activeTask && (
          <div className="w-64 rounded-xl border border-secondary-200 bg-secondary-50 p-3 shadow-xl opacity-95">
            <div className="text-sm font-medium text-primary-900 leading-snug">{activeTask.name}</div>
            <div className="mt-2 flex items-center gap-1 text-xs text-gray-400">
              <TbCalendar className="h-3.5 w-3.5" />
              <span>{activeTask.dueDate ? formatDateAndTime(new Date(activeTask.dueDate)) : '—'}</span>
            </div>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}

function BoardColumn({
  status,
  tasks,
  allStatuses,
  handleUpdate,
  refreshTable,
  communityId,
}: {
  status: TaskStatus;
  tasks: Task[];
  allStatuses: TaskStatus[];
  handleUpdate: (task: Task) => void;
  refreshTable: () => void;
  communityId?: number;
}) {
  const { t: tCol } = useTranslation('', { keyPrefix: 'page.lists.task' });
  const { setNodeRef, isOver } = useDroppable({
    id: String(status.statusId),
    data: { type: 'column', statusId: status.statusId },
  });

  return (
    <div
      ref={setNodeRef}
      className={twMerge(
        'flex w-72 flex-shrink-0 flex-col overflow-hidden rounded-[12px] border border-secondary-200 bg-surface transition-colors',
        isOver && 'ring-2 ring-primary-100',
      )}>
      {/* Column header */}
      <div className="flex items-center justify-between bg-primary-100 px-4 py-2.5">
        <div className="flex items-center gap-2">
          <span
            className="inline-block h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: status.color }}
          />
          <span className="text-xs font-semibold uppercase tracking-wider text-primary-900">
            {getStatusLabel(status, tCol)}
          </span>
        </div>
        <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-surface px-1.5 text-xs font-semibold text-primary-900">
          {tasks.length}
        </span>
      </div>

      {/* Tasks */}
      <div className="flex min-h-[120px] flex-1 flex-col gap-2 overflow-y-auto p-3">
        {tasks.length === 0 ? (
          <div className="flex flex-1 items-center justify-center py-8 text-xs text-gray-400">
            {tCol('noTasks')}
          </div>
        ) : (
          tasks.map((taskItem) => (
            <BoardTaskCard
              key={taskItem.taskId}
              task={taskItem}
              statuses={allStatuses}
              handleUpdate={handleUpdate}
              refreshTable={refreshTable}
              communityId={communityId}
            />
          ))
        )}
      </div>
    </div>
  );
}

function BoardTaskCard({
  task,
  statuses,
  isOverlay = false,
  handleUpdate,
  refreshTable,
  communityId,
}: {
  task: Task;
  statuses: TaskStatus[];
  isOverlay?: boolean;
  handleUpdate: (task: Task) => void;
  refreshTable: () => void;
  communityId?: number;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: String(task.taskId),
    data: { type: 'task', taskId: task.taskId },
  });

  const style = {
    transform: isDragging ? undefined : CSS.Transform.toString(transform),
  };

  const deleteTask = () => {
    if (!task.taskId) return;
    taskService.deleteTask(task.taskId).then(refreshTable);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={twMerge(
        'cursor-grab select-none rounded-[8px] bg-secondary-200/70 p-3 transition-colors active:cursor-grabbing hover:bg-secondary-200',
        isDragging && 'opacity-0',
        isOverlay && 'rotate-2 shadow-xl',
      )}>
      <div className="flex items-start gap-2">
        <StatusMenu
          task={task}
          statuses={statuses}
          communityId={communityId}
          onChange={refreshTable}
        />
        <p className="flex-1 text-sm font-medium text-primary-900 leading-snug">
          {task.name}
        </p>
      </div>

      <div className="mt-2 flex items-center justify-between">
        <div className="flex items-center gap-1 text-xs text-gray-400">
          <TbCalendar className="h-3.5 w-3.5" />
          <span>{task.dueDate ? formatDateAndTime(new Date(task.dueDate)) : '—'}</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => handleUpdate(task)}
            className="rounded p-1 text-gray-400 hover:bg-secondary-200 hover:text-primary-900">
            <TbEdit className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={deleteTask}
            className="rounded p-1 text-gray-400 hover:bg-secondary-200 hover:text-danger">
            <TbTrash className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {task.assignees && task.assignees.length > 0 && (
        <div className="mt-2 flex items-center gap-1">
          {task.assignees.slice(0, 3).map((a) => (
            <div
              key={a.userId}
              className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-900 text-[9px] font-bold text-white"
              title={`${a.firstName} ${a.lastName}`}>
              {a.firstName[0]}{a.lastName[0]}
            </div>
          ))}
          {task.assignees.length > 3 && (
            <span className="text-xs text-gray-400">+{task.assignees.length - 3}</span>
          )}
        </div>
      )}
    </div>
  );
}
